import type { ByecodeSize, PopoverWidth } from '@byecode/ui'
import { Popover } from '@byecode/ui'
import type { DataSourceAbstract, Field, TypeInstanceMap } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { useDisclosure } from '@mantine/hooks'
import { useAbortAll } from '@rpldy/shared-ui'
import React, { useCallback, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import styled, { css } from 'styled-components'

import { getInputInnerTypeMatch } from '../../../utils'
import { ignoreEleId } from '../constant'
import { VariableSelectDropDown, type VariableSelectDropDownProps } from '../VariableSelectDropDown'
import { VariableInput } from './Input'
import SelectDataSourceModal from './SelectDataSource'

const SCxContainer = styled.div<{ highlighting?: boolean }>`
    ${({ highlighting }) =>
        highlighting &&
        css`
            outline: 1px solid var(--color-main);
            border-radius: 6px;
            box-shadow: 0 0 4px 0 var(--color-main);
            overflow: hidden;
        `}
`

interface VariableSelectProps extends Omit<VariableSelectDropDownProps, 'fields' | 'prefix'> {
    innerTypes?: TypeInstanceMap[]
    size?: ByecodeSize
    placeholder?: string
    field?: Field
    dataSourceList?: DataSourceAbstract[]
    style?: React.CSSProperties
    width?: string
    withinPortal?: boolean
    popoverWidth?: PopoverWidth
    disabled?: boolean
    highlighting?: boolean
    prefix?: React.ReactNode
    wrapperStyle?: React.CSSProperties
    focusOutLine?: boolean
}

export const VariableSelect: React.FC<VariableSelectProps> = ({
    size,
    placeholder,
    field,
    dataSourceList,
    style,
    width,
    multiple,
    withinPortal = true,
    popoverWidth = 248,
    disabled,
    highlighting,
    prefix,
    wrapperStyle,
    focusOutLine,

    ...dropDownProps
}) => {
    const resolvedInnerTypes = (() => {
        if (dropDownProps.innerTypes && dropDownProps.innerTypes.length > 0) {
            return dropDownProps.innerTypes
        }
        if (!field?.innerType) {
            return undefined
        }
        const matched = getInputInnerTypeMatch(field.innerType)
        if (!matched) {
            return undefined
        }
        return Array.isArray(matched) ? matched : [matched]
    })()
    const resolvedDropDownProps = resolvedInnerTypes ? { ...dropDownProps, innerTypes: resolvedInnerTypes } : dropDownProps
    const {
        value,
        onChange,
        formOption,
        pageOptions,
        userOption,
        viewOptions,
        eventOption,
        loopOption,
        inputOption,
        filterOption,
        upstreamOptions,
        datasourceOption,
        datasourceOptions,
        imageOption,
        selectDataSourceProps
    } = resolvedDropDownProps
    const fields = field ? [field] : undefined

    const [opened, { close, open, toggle }] = useDisclosure(false)
    const [modalOpened, setModalOpened] = useState(false)
    const dropDownRef = useRef<HTMLDivElement>(null)
    const [uploadProgressPercent, setUploadProgressPercent] = useState(-1)
    const isCancelRef = useRef(false)

    const abortAllTask = useAbortAll()

    const handleCancelUpload = useCallback(() => {
        abortAllTask()
        isCancelRef.current = true
        setUploadProgressPercent(-1)
        setTimeout(() => {
            isCancelRef.current = false
        }, 300)
    }, [abortAllTask])

    const handleToggleOpen = useCallback(() => {
        // const emptyOption = !options || options.flatMap(option => option.list).length === 0

        // if (!opened && emptyOption) {
        //     return
        // }
        toggle()
    }, [toggle])

    useClickAway(dropDownRef, e => {
        const { target } = e
        if (target instanceof Element) {
            if (document.querySelector(`#${ignoreEleId}`)?.contains(target)) {
                return
            }
            close()
        }
    })

    return (
        <Popover opened={opened} disabled={disabled} withinPortal={withinPortal} width={popoverWidth} position="bottom-end" onClose={close}>
            <Popover.Target>
                <SCxContainer highlighting={highlighting} style={style}>
                    <div style={{ width: '100%' }}>
                        <VariableInput
                            size={size}
                            disabled={disabled}
                            value={value}
                            field={field}
                            pageOptions={pageOptions}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            inputOption={inputOption}
                            filterOption={filterOption}
                            viewOptions={viewOptions}
                            formOption={formOption}
                            eventOption={eventOption}
                            loopOption={loopOption}
                            datasourceOption={datasourceOption}
                            datasourceOptions={datasourceOptions}
                            imageOption={imageOption}
                            width={width}
                            opened={opened}
                            placeholder={placeholder}
                            dataSourceList={dataSourceList}
                            filePercent={uploadProgressPercent}
                            prefix={prefix}
                            wrapperStyle={wrapperStyle}
                            focusOutLine={focusOutLine}
                            onChangeOpen={handleToggleOpen}
                            onCancelUpload={handleCancelUpload}
                            onChange={onChange}
                            onOpenSelectDataSourceModal={() => setModalOpened(true)}
                        />
                        {selectDataSourceProps && (
                            <SelectDataSourceModal
                                opened={modalOpened}
                                variable={value?.type === VariableType.SELECT_DATASOURCE ? value : undefined}
                                dataSourceList={dataSourceList}
                                {...selectDataSourceProps}
                                onClose={() => setModalOpened(false)}
                                onConfirm={onChange}
                            />
                        )}
                    </div>
                </SCxContainer>
            </Popover.Target>
            <Popover.Dropdown>
                <VariableSelectDropDown
                    ref={dropDownRef}
                    {...resolvedDropDownProps}
                    fields={fields}
                    onChange={v => {
                        if (v.type === VariableType.SELECT_DATASOURCE) {
                            setModalOpened(true)
                            return
                        }
                        onChange(v)
                        close()
                    }}
                    onUploadProgressChange={percent => {
                        !isCancelRef.current && setUploadProgressPercent(percent)
                    }}
                    onOpenSelectDataSourceModal={() => setModalOpened(true)}
                />
            </Popover.Dropdown>
        </Popover>
    )
}
